import React from 'react';
import Skeleton from './Skeleton';

// ── Shared primitives ────────────────────────────────────────────────────────

const KpiCardSkel = () => (
    <div className="bg-white px-5 py-5 rounded-2xl flex items-center gap-4" style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
        <Skeleton.Box width={46} height={46} radius={12} />
        <div className="flex-1 min-w-0 space-y-2">
            <Skeleton.Box width="55%" height={10} radius={4} />
            <Skeleton.Box width="30%" height={30} radius={6} />
            <Skeleton.Box width="65%" height={10} radius={4} />
        </div>
    </div>
);

const BreakdownCardSkel = ({ rows = 4, className = '' }: { rows?: number; className?: string }) => (
    <div className={`bg-white rounded-2xl overflow-hidden flex flex-col ${className}`} style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-2">
                <Skeleton.Box width={24} height={24} radius={8} />
                <Skeleton.Box width="45%" height={10} radius={4} />
            </div>
            <Skeleton.Box width={36} height={20} radius={999} />
        </div>
        <ul className="divide-y divide-gray-50 flex-1">
            {[70, 45, 90, 30].slice(0, rows).map((w, i) => (
                <li key={i} className="grid items-center gap-4 px-4 py-2.5"
                    style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}>
                    <Skeleton.Box width="70%" height={10} radius={4} />
                    <Skeleton.Box width={`${w}%`} height={6} radius={999} />
                    <Skeleton.Box width="100%" height={10} radius={4} />
                </li>
            ))}
        </ul>
    </div>
);

// ── Matrix skeleton — mimics the 5×5 coloured grid ──────────────────────────

const MatrixSkel = () => (
    <div className="bg-white p-3 rounded-2xl flex flex-col" style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
        {/* header */}
        <div className="flex items-center gap-2 mb-3">
            <Skeleton.Box width={32} height={32} radius={8} />
            <div className="flex-1 space-y-1.5">
                <Skeleton.Box width="55%" height={11} radius={4} />
                <Skeleton.Box width="80%" height={9} radius={4} />
            </div>
        </div>
        {/* grid */}
        <div className="flex flex-1 mt-1">
            <div className="w-7 flex items-center justify-center">
                <Skeleton.Box width={8} height={80} radius={4} />
            </div>
            <div className="flex-1 grid grid-cols-5 gap-1 aspect-square">
                {Array.from({ length: 25 }).map((_, i) => (
                    <Skeleton.Box key={i} width="100%" height="100%" radius={6} />
                ))}
            </div>
        </div>
        {/* x-axis label */}
        <div className="flex justify-center mt-2">
            <Skeleton.Box width={60} height={9} radius={4} />
        </div>
    </div>
);

// ── Governance / generic insights skeleton ───────────────────────────────────

interface InsightsSkeletonProps {
    kpiCount?: number;
    breakdownCount?: number;
    breakdownCols?: number;
}

const InsightsSkeleton: React.FC<InsightsSkeletonProps> = ({
    kpiCount = 4,
    breakdownCount = 6,
    breakdownCols = 3,
}) => (
    <div className="py-4 space-y-4">
        <div className={`grid gap-4 grid-cols-2 lg:grid-cols-${kpiCount}`}>
            {Array.from({ length: kpiCount }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        <div className={`grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-${breakdownCols}`}>
            {Array.from({ length: breakdownCount }).map((_, i) => <BreakdownCardSkel key={i} />)}
        </div>
    </div>
);

// ── Risk insights skeleton — matches exact Risk page layout ──────────────────

export const RiskInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        {/* 4 KPI cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>

        {/* Legend row + matrices + distributions */}
        <div className="flex gap-4 items-stretch">

            {/* Left: legend strip + two matrices */}
            <div className="flex flex-col gap-4 flex-1 min-w-0">
                {/* Legend strip */}
                <div className="flex items-center justify-between px-1">
                    <Skeleton.Box width="45%" height={10} radius={4} />
                    <div className="flex gap-4">
                        {[40, 28, 30, 32, 46].map((w, i) => (
                            <Skeleton.Box key={i} width={w} height={10} radius={4} />
                        ))}
                    </div>
                </div>
                {/* Two matrix cards side by side */}
                <div className="grid grid-cols-2 gap-4 flex-1">
                    <MatrixSkel />
                    <MatrixSkel />
                </div>
            </div>

            {/* Right: 3 distribution cards */}
            <div className="flex flex-col gap-4 shrink-0" style={{ width: 'calc(25% - 0.375rem)' }}>
                <BreakdownCardSkel rows={5} />
                <BreakdownCardSkel rows={2} />
                <BreakdownCardSkel rows={5} className="flex-1" />
            </div>

        </div>
    </div>
);

// ── Control insights skeleton — matches exact Control page layout ─────────────

export const ControlInsightsSkeleton: React.FC = () => (
    <div className="py-4 px-2">
        <div className="flex gap-4 items-stretch">

            {/* Left: matrix card — fixed width matching real layout */}
            <div className="shrink-0 bg-white rounded-2xl p-2.5 flex flex-col gap-2" style={{ width: '390px', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
                {/* header */}
                <div className="flex items-center gap-2">
                    <Skeleton.Box width={28} height={28} radius={8} />
                    <div className="flex-1 space-y-1">
                        <Skeleton.Box width="60%" height={10} radius={4} />
                        <Skeleton.Box width="80%" height={8} radius={4} />
                    </div>
                </div>
                {/* legend */}
                <div className="flex gap-4 pl-1">
                    {[28, 28, 24, 28, 32].map((w, i) => (
                        <Skeleton.Box key={i} width={w} height={8} radius={4} />
                    ))}
                </div>
                {/* 6×6 grid */}
                <div className="flex flex-1 mt-1">
                    <div className="w-6 flex items-center justify-center">
                        <Skeleton.Box width={8} height={72} radius={4} />
                    </div>
                    <div className="grid gap-1" style={{ gridTemplateRows: 'repeat(6, 48px)', gridTemplateColumns: 'repeat(6, 48px)' }}>
                        {Array.from({ length: 36 }).map((_, i) => (
                            <Skeleton.Box key={i} width="100%" height="100%" radius={6} />
                        ))}
                    </div>
                </div>
                {/* x-axis */}
                <div className="flex justify-center">
                    <Skeleton.Box width={56} height={8} radius={4} />
                </div>
            </div>

            {/* Right: 4 KPI cards (single row) + 2 distributions side by side */}
            <div className="flex flex-col gap-4 flex-1 min-w-0">
                {/* 4 KPI cards in one row */}
                <div className="grid grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
                </div>
                {/* 2 distribution cards side by side */}
                <div className="flex gap-4 flex-1 items-stretch">
                    <BreakdownCardSkel rows={4} className="flex-1" />
                    <BreakdownCardSkel rows={4} className="flex-1" />
                </div>
            </div>

        </div>
    </div>
);

// ── CAPA insights skeleton — 3 KPI cards + 4 distribution cards + recurrent ──

export const CapaInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        <div className="grid grid-cols-2 gap-4">
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
        </div>
        <BreakdownCardSkel rows={4} />
    </div>
);

// ── Incident insights skeleton — 3 KPI cards + 2 distribution cards ──────────

export const IncidentInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        <div className="grid grid-cols-2 gap-4">
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
        </div>
    </div>
);

// ── Monitoring insights skeleton — 3 KPI cards + distribution grids ──────────

export const MonitoringInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        {/* 3 KPI cards */}
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        {/* 2 distribution cards side by side */}
        <div className="grid grid-cols-2 gap-4">
            <BreakdownCardSkel rows={6} />
            <BreakdownCardSkel rows={6} />
        </div>
        {/* 3 distribution cards */}
        <div className="grid grid-cols-3 gap-4">
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
        </div>
    </div>
);

// ── Audit insights skeleton — 4 KPI cards + 1 breakdown card ─────────────────

export const AuditInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        {/* 4 KPI cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        {/* Programs by Status — width matches 1 KPI card */}
        <div style={{ width: 'calc(25% - 0.375rem)' }}>
            <BreakdownCardSkel rows={6} />
        </div>
    </div>
);

// ── Compliance insights skeleton — 4 KPI cards + trend chart + status dist ────

export const ComplianceInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        {/* 4 KPI cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>

        {/* Trend chart + status distribution */}
        <div className="flex gap-4 items-stretch">
            {/* Trend chart */}
            <div className="flex-1 min-w-0 bg-white rounded-2xl overflow-hidden flex flex-col" style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-white">
                    <Skeleton.Box width={13} height={13} radius={4} />
                    <Skeleton.Box width="45%" height={10} radius={4} />
                </div>
                <div className="flex-1 px-4 py-3 flex items-end justify-between gap-4" style={{ minHeight: '220px' }}>
                    {[0, 0, 0, 0, 0, 80].map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                            <Skeleton.Box width="100%" height={8} radius={4} />
                            <Skeleton.Box width="100%" height={`${Math.max(h, 6)}%`} radius={4} />
                            <Skeleton.Box width={24} height={8} radius={4} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Status distribution */}
            <div className="shrink-0" style={{ width: 'calc(25% - 0.375rem)' }}>
                <BreakdownCardSkel rows={4} className="h-full" />
            </div>
        </div>
    </div>
);

// ── Project Management insights skeleton — 4 KPI cards + 3 distributions + 2 tables ──

export const PrmInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        <div className="grid grid-cols-3 gap-4">
            <BreakdownCardSkel rows={5} />
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
        </div>
        <BreakdownCardSkel rows={4} />
        <BreakdownCardSkel rows={4} />
    </div>
);

// ── Cadence insights skeleton — 4 KPI cards + 3 distribution cards ───────────

export const CadenceInsightsSkeleton: React.FC = () => (
    <div className="py-4 space-y-4">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <KpiCardSkel key={i} />)}
        </div>
        <div className="grid grid-cols-3 gap-4">
            <BreakdownCardSkel rows={5} />
            <BreakdownCardSkel rows={4} />
            <BreakdownCardSkel rows={4} />
        </div>
    </div>
);

export default InsightsSkeleton;
